import { Injectable } from '@angular/core';
import { MegaMenuSectionLabel, ProductUid, ResourcesSectionLabel, UserRoleUid } from '@constants';
import { ChildPageRoute, GknRouteLabel, GknRoutePath } from '../app-routing.enum';
import { MegaMenuItem, MegaMenuSection } from './app-mega-menu/app-mega-menu.component';

@Injectable({
    providedIn: 'root',
})
export class AppHeaderSharedService {
    readonly routes = [
        { routerLink: `/${GknRoutePath.ADVOCACY}`, label: GknRouteLabel.ADVOCACY },
        { routerLink: `/${GknRoutePath.COMMUNITY}`, label: GknRouteLabel.COMMUNITY },
        { routerLink: `/${GknRoutePath.EDUCATION}`, label: GknRouteLabel.EDUCATION },
        { routerLink: `/${GknRoutePath.RESOURCES}`, label: GknRouteLabel.RESOURCES },
        { routerLink: `/${GknRoutePath.SUPPORT}`, label: GknRouteLabel.SUPPORT },
    ];

    readonly nonClickableMenus = [GknRouteLabel.RESOURCES, GknRouteLabel.SUPPORT];
    readonly GET_STARTED_MENU_SECTION: MegaMenuSection = {
        title: MegaMenuSectionLabel.GET_STARTED,
        items: [
            {
                // Shows only on Genesys Cloud
                label: ResourcesSectionLabel.ONBOARDING,
                routerLink: `/${GknRoutePath.RESOURCES}/${ChildPageRoute.ONBOARDING}`,
                productUid: [ProductUid.GENESYS_CLOUD_CX],
            },
        ],
    };

    readonly ADOPT_MENU_SECTION: MegaMenuSection = {
        title: MegaMenuSectionLabel.ADOPT,
        items: [
            {
                // Shows only on Genesys Cloud
                label: ResourcesSectionLabel.CAPABILITY_ACCELERATOR,
                routerLink: `/${GknRoutePath.RESOURCES}/${ChildPageRoute.CAPABILITY_ACCELERATOR}`,
                productUid: [ProductUid.GENESYS_CLOUD_CX],
            },
            // {
            //     // Available for all products
            //     label: ResourcesSectionLabel.CX_EVOLUTION,
            //     routerLink: `/${GknRoutePath.RESOURCES}/${ChildPageRoute.CX_EVOLUTION}`,
            // },
        ],
    };

    readonly EXPLORE_MENU_SECTION: MegaMenuSection = {
        title: MegaMenuSectionLabel.EXPLORE,
        items: [
            {
                // Available for all products
                label: ResourcesSectionLabel.PRODUCT_KNOWLEDGE,
                routerLink: `/${GknRoutePath.RESOURCES}/${ChildPageRoute.PRODUCT_KNOWLEDGE}`,
            },
            {
                // Shows only on Genesys Cloud
                label: ResourcesSectionLabel.APPFOUNDRY,
                routerLink: `/${GknRoutePath.RESOURCES}/${ChildPageRoute.APPFOUNDRY}`,
                productUid: [ProductUid.GENESYS_CLOUD_CX],
            },
            {
                // Available for all products
                label: ResourcesSectionLabel.GENESYS_NEWS_AND_EVENTS,
                routerLink: `/${GknRoutePath.RESOURCES}/${ChildPageRoute.GENESYS_NEWS_AND_EVENTS}`,
            },
            {
                // Shows only on Genesys Cloud
                label: ResourcesSectionLabel.UX_RESEARCH_PROGRAM,
                href: 'https://www.genesys.com/uxresearch',
                productUid: [ProductUid.GENESYS_CLOUD_CX],
            },
        ],
    };

    readonly QUICK_LINKS_MENU_SECTION: MegaMenuSection = {
        title: MegaMenuSectionLabel.QUICK_LINKS,
        items: [
            {
                // Shows only for Genesys Cloud
                label: ResourcesSectionLabel.BEST_PRACTICES,
                href: 'https://help.mypurecloud.com/articles/best-practices-smart-use-cases/',
                loadsInframe: true,
                productUid: [ProductUid.GENESYS_CLOUD_CX],
            },
            {
                // Shows only for Genesys Engage On-Premises
                label: ResourcesSectionLabel.BEST_PRACTICES,
                href: 'https://docs.genesys.com/Documentation/Best',
                productUid: [ProductUid.GENESYS_ENGAGE_ON_PREM],
            },
            {
                label: ResourcesSectionLabel.INDUSTRY_TERMS,
                href: 'https://www.genesys.com/definitions',
            },
            {
                // Shows only for Genesys Cloud
                label: ResourcesSectionLabel.TROUBLESHOOTING,
                href: 'https://help.mypurecloud.com/articles/about-genesys-cloud-troubleshoot/',
                loadsInframe: true,
                productUid: [ProductUid.GENESYS_CLOUD_CX],
            },
            {
                label: ResourcesSectionLabel.PARTNER_PORTAL,
                href: 'https://genesys.okta.com/home/salesforce/0oa1dx5zui7iLqPcm0h8/46',
                authed: true,
                portalType: UserRoleUid.PARTNER,
            },
        ],
    };

    /**
     * Return boolean if Mega Menu Item can show portal links based on user access
     * @param item - Megamenu Item
     * @param access accessable parameters
     * @returns boolean if portal links should be shown
     */
    shouldShowPortal(
        item: MegaMenuItem,
        access: { showConsultantPortal: boolean; showPartnerPortal: boolean },
    ): boolean {
        if (!item.portalType) return true;
        return item.portalType === UserRoleUid.CONSULTANT ? access.showConsultantPortal : access.showPartnerPortal;
    }

    /**
     * Return boolean if route is clickable
     * @param routeLabel - route label string
     * @returns boolean if route label includes a defined GKN route label
     */
    isClickableRoute(routeLabel: string): boolean {
        return !this.nonClickableMenus?.includes(routeLabel as GknRouteLabel);
    }

    /**
     * Return boolean if menuItem matches a mega menu
     * @param menuItem - current menu item string
     * @returns boolean if mega menu matches resources or support
     */
    hasMegaMenu(menuItem: string | null): boolean {
        return menuItem === GknRouteLabel.RESOURCES || menuItem === GknRouteLabel.SUPPORT;
    }

    /**
     * Return boolean if route is active
     * @param routerLink - router link string
     * @param activePage - active page string
     * @returns boolean
     */
    isRouteActive(routerLink: string, activePage: string): boolean {
        return activePage === routerLink || activePage?.startsWith(routerLink + '/');
    }

    /**
     * Filter menu items based on product ID
     * @param section - MegaMenuSection to filter
     * @param productUid - Product ID to filter by
     * @returns filtered MegaMenuSection
     */
    private filterMenuByProduct(section: MegaMenuSection, productUid: string): MegaMenuSection {
        const filteredItems = section.items?.filter((item) => {
            // If item has productUid, filter by it
            if (item.productUid) {
                return item.productUid.includes(productUid as any);
            }
            // If no productUid, show item unless restricted by portalType
            // (portalType filtering is handled separately in shouldShowPortal)
            return true;
        });

        return { ...section, items: filteredItems };
    }

    /**
     * Return product specific resource menu section and related menu items
     * @param productUid - Product string
     * @returns collection of MegaMenuSection
     */
    getResourcesMenu(productUid?: string): MegaMenuSection[] {
        // Default to Genesys Cloud CX if no product is provided
        const selectedProductUid = productUid || ProductUid.GENESYS_CLOUD_CX;

        const baseMenu = [
            this.filterMenuByProduct(this.GET_STARTED_MENU_SECTION, selectedProductUid),
            this.filterMenuByProduct(this.ADOPT_MENU_SECTION, selectedProductUid),
            this.filterMenuByProduct(this.EXPLORE_MENU_SECTION, selectedProductUid),
            this.filterMenuByProduct(this.QUICK_LINKS_MENU_SECTION, selectedProductUid),
        ];

        switch (selectedProductUid) {
            case ProductUid.GENESYS_CLOUD_CX:
                return baseMenu;
            case ProductUid.GENESYS_ENGAGE_ON_PREM:
            default:
                return baseMenu; // Return Genesys Cloud menu as default
        }
    }

    /**
     * Return product specific support menu sections
     * @param productUid - Product string
     * @param hasSupportAccess - Whether user has MySupport permission
     * @returns collection of MegaMenuSection
     */
    getSupportMenu(productUid?: string, hasSupportAccess?: boolean): MegaMenuSection[] {
        const _selectedProductUid = productUid || ProductUid.GENESYS_CLOUD_CX;

        const getHelpItems: MegaMenuItem[] = [
            ...(hasSupportAccess
                ? [
                      {
                          label: 'Case management',
                          routerLink: `/${GknRoutePath.SUPPORT}/${ChildPageRoute.CASE_MANAGEMENT}`,
                          authed: true,
                      },
                  ]
                : []),
            {
                label: 'Product Support resources',
                routerLink: `/${GknRoutePath.SUPPORT}/${ChildPageRoute.PRODUCT_SUPPORT_RESOURCES}`,
            },
        ];

        const supportMenu: MegaMenuSection[] = [
            {
                title: 'Get Help',
                items: getHelpItems,
            },
            {
                title: 'Stay Informed',
                items: [
                    {
                        label: 'Product announcements',
                        routerLink: `/${GknRoutePath.SUPPORT}/${ChildPageRoute.PRODUCT_ANNOUNCEMENTS}`,
                    },
                ],
            },
            {
                title: 'Case management',
                type: 'spotlight',
                content: {
                    image: 'assets/images/case-management.svg',
                    title: '',
                    description: 'Sign in to view and manage your support cases.',
                    buttonText: 'Sign in now',
                    buttonAction: 'signIn',
                },
            },
        ];

        return supportMenu;
    }
}
